<template>
    <div class="dv2-extras">
        <router-link to="/plano" class="dv2-plan" :title="planName">
            {{ planName }}
        </router-link>

        <button
            type="button"
            class="dv2-icon-btn dv2-icon-btn--ghost"
            :title="themeButtonTitle"
            :aria-label="themeButtonTitle"
            @click="cycleThemeMode"
        >
            <span v-html="themeModeIcon"></span>
        </button>

        <div class="dv2-dropdown" ref="notifyWrap">
            <button
                type="button"
                class="dv2-icon-btn dv2-icon-btn--ghost dv2-notify-btn"
                title="Notificações"
                aria-label="Notificações"
                :aria-expanded="notifyOpen"
                @click="toggleNotify"
            >
                <span v-html="bellIcon"></span>
                <span
                    v-if="notificacoesStore.unreadCount > 0"
                    class="dv2-notify-badge"
                    :class="{
                        'is-wide': notificacoesStore.unreadCount > 9 && notificacoesStore.unreadCount <= 99,
                        'is-extra': notificacoesStore.unreadCount > 99,
                    }"
                >
                    {{ notificacoesStore.unreadCount > 99 ? '99+' : notificacoesStore.unreadCount }}
                </span>
            </button>

            <div v-if="notifyOpen" class="dv2-dropdown__panel dv2-notify-panel" role="menu">
                <div class="dv2-notify-panel__head">
                    <strong>Notificações</strong>
                    <span v-if="notificacoesStore.unreadCount > 0" class="dv2-notify-panel__count">
                        {{ notificacoesStore.unreadCount }}
                    </span>
                </div>

                <div class="dv2-notify-panel__list" ref="notifyScrollRef">
                    <div v-if="notificacoesStore.isInitialLoading" class="dv2-notify-empty">
                        Carregando...
                    </div>
                    <template v-else-if="notificacoesStore.notifications.length">
                        <div
                            v-for="notification in notificacoesStore.notifications"
                            :key="notification.id"
                            class="dv2-notify-item"
                            :class="{ 'is-unread': !notification.read_at }"
                            role="button"
                            tabindex="0"
                            @click="onNotificationClick(notification)"
                            @keydown.enter.prevent="onNotificationClick(notification)"
                        >
                            <div class="dv2-notify-item__body">
                                <p class="dv2-notify-item__title">
                                    {{ notification.data?.title || 'Notificação' }}
                                </p>
                                <p
                                    class="dv2-notify-item__msg"
                                    :class="{ 'is-expanded': expandedNotifications[notification.id] }"
                                >
                                    {{ notification.data?.message || '' }}
                                </p>
                                <div class="dv2-notify-item__meta">
                                    <button
                                        v-if="shouldShowExpandButton(notification.data?.message)"
                                        type="button"
                                        class="dv2-notify-item__expand"
                                        @click.stop="toggleNotificationExpand(notification.id)"
                                    >
                                        {{ expandedNotifications[notification.id] ? 'Ver menos' : 'Ver tudo' }}
                                    </button>
                                    <small>{{ formatTimeAgo(notification.created_at) }}</small>
                                </div>
                            </div>
                            <button
                                type="button"
                                class="dv2-notify-item__remove"
                                title="Remover"
                                aria-label="Remover notificação"
                                @click.stop="onDeleteNotification(notification.id)"
                            >×</button>
                        </div>
                        <div
                            v-if="notificacoesStore.hasMore"
                            ref="notifyLoadMoreSentinel"
                            class="dv2-notify-sentinel"
                            aria-hidden="true"
                        ></div>
                        <div v-if="notificacoesStore.loadingMore" class="dv2-notify-loading-more">
                            Carregando mais...
                        </div>
                    </template>
                    <div v-else class="dv2-notify-empty">Nenhuma notificação</div>
                </div>

                <button
                    v-if="!notificacoesStore.isInitialLoading && notificacoesStore.notifications.length"
                    type="button"
                    class="dv2-notify-panel__footer"
                    @click="onMarkAllRead"
                >
                    Marcar todas como lidas
                </button>
            </div>
        </div>

        <div class="dv2-dropdown" ref="userWrap">
            <button
                type="button"
                class="dv2-user-btn"
                :aria-expanded="userOpen"
                aria-haspopup="menu"
                @click="toggleUser"
            >
                <ProfileAvatar
                    :src="user?.profilePic"
                    :name="user?.name"
                    alt="avatar"
                    size="32px"
                />
            </button>

            <div v-if="userOpen" class="dv2-dropdown__panel dv2-user-panel" role="menu">
                <router-link to="/meus-dados" class="dv2-user-panel__item" @click="userOpen = false">
                    Minha conta
                </router-link>
                <router-link to="/preferencias" class="dv2-user-panel__item" @click="userOpen = false">
                    Preferências
                </router-link>
                <router-link to="/faturas" class="dv2-user-panel__item" @click="userOpen = false">
                    Faturas
                </router-link>
                <button type="button" class="dv2-user-panel__item dv2-user-panel__item--danger" @click="logout">
                    Sair
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ProfileAvatar from '@/views/components/ProfileAvatar.vue';
import { useNotificacoesStore } from '@/stores/notificacoes';
import { useNotificationInfiniteScroll } from '@/composables/useNotificationInfiniteScroll';
import { dashboardV2Icons } from '@/components/dashboard-v2/navConfig';

const store = useStore();
const router = useRouter();
const notificacoesStore = useNotificacoesStore();

const notifyOpen = ref(false);
const userOpen = ref(false);
const notifyWrap = ref(null);
const userWrap = ref(null);
const expandedNotifications = ref({});
const notifyScrollRef = ref(null);
const { sentinelRef: notifyLoadMoreSentinel } = useNotificationInfiniteScroll(
    notifyScrollRef,
    notifyOpen
);

const bellIcon = dashboardV2Icons.bell;

const user = computed(() => store.state.auth?.user);
const themeMode = computed(() => store.state.dark_mode || 'light');
const planName = computed(
    () => user.value?.active_subscription?.plan?.name || 'Professor Iniciante'
);

const themeModeIcon = computed(() => {
    if (themeMode.value === 'dark') return dashboardV2Icons.moon;
    if (themeMode.value === 'system') return dashboardV2Icons.monitor;
    return dashboardV2Icons.sun;
});

const themeButtonTitle = computed(() => {
    if (themeMode.value === 'light') return 'Tema claro — clique para escuro';
    if (themeMode.value === 'dark') return 'Tema escuro — clique para padrão do sistema';
    return 'Padrão do sistema — clique para claro';
});

function cycleThemeMode() {
    notifyOpen.value = false;
    userOpen.value = false;
    const current = themeMode.value;
    const next = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
    if (window.$appSetting?.toggleMode) {
        window.$appSetting.toggleMode(next);
    } else {
        store.commit('toggleDarkMode', next);
    }
}

function toggleNotify() {
    userOpen.value = false;
    notifyOpen.value = !notifyOpen.value;
    if (notifyOpen.value) {
        notificacoesStore.fetchNotifications({ reset: true }).catch(() => {});
    }
}

function toggleUser() {
    notifyOpen.value = false;
    userOpen.value = !userOpen.value;
}

function formatTimeAgo(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'agora';
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `há ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `há ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `há ${diffInDays} ${diffInDays === 1 ? 'dia' : 'dias'}`;
    return date.toLocaleDateString('pt-BR');
}

function shouldShowExpandButton(message) {
    if (!message) return false;
    return String(message).length > 100;
}

function toggleNotificationExpand(notificationId) {
    expandedNotifications.value[notificationId] = !expandedNotifications.value[notificationId];
}

async function onNotificationClick(notification) {
    if (!notification.read_at) {
        try {
            await notificacoesStore.markAsRead(notification.id);
        } catch {
            /* ignore */
        }
    }
    if (notification.data?.action_url) {
        try {
            const url = new URL(notification.data.action_url);
            window.open(url.href, '_blank', 'noopener,noreferrer');
        } catch {
            window.open(
                window.location.origin + notification.data.action_url,
                '_blank',
                'noopener,noreferrer'
            );
        }
    }
}

async function onDeleteNotification(id) {
    try {
        await notificacoesStore.deleteNotification(id);
    } catch {
        /* ignore */
    }
}

async function onMarkAllRead() {
    try {
        await notificacoesStore.markAllAsRead();
    } catch {
        /* ignore */
    }
}

async function logout() {
    userOpen.value = false;
    try {
        notificacoesStore.stopPolling();
        notificacoesStore.stopListening();
        notificacoesStore.clear();
        await store.dispatch('auth/LogOut');
    } catch {
        /* ignore */
    }
    router.push({ name: 'login' });
}

function startNotificationsIfAuthenticated() {
    const u = store.state.auth?.user;
    if (!u) return;
    if (!notificacoesStore.isPolling) {
        notificacoesStore.startPolling(30000);
    }
    if (!notificacoesStore.isListening) {
        const userId = u.id || u.uuid;
        if (userId) notificacoesStore.startListening(userId);
    }
}

function onDocClick(event) {
    const t = event.target;
    if (notifyOpen.value && notifyWrap.value && !notifyWrap.value.contains(t)) {
        notifyOpen.value = false;
    }
    if (userOpen.value && userWrap.value && !userWrap.value.contains(t)) {
        userOpen.value = false;
    }
}

watch(
    () => store.state.auth?.user,
    (newUser) => {
        if (newUser) {
            startNotificationsIfAuthenticated();
        } else {
            notificacoesStore.stopPolling();
            notificacoesStore.stopListening();
        }
    },
    { immediate: true }
);

onMounted(() => {
    startNotificationsIfAuthenticated();
    document.addEventListener('click', onDocClick);
    const unlock = () => notificacoesStore.unlockAudioContext?.();
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
});

onUnmounted(() => {
    document.removeEventListener('click', onDocClick);
});
</script>

<style scoped>
.dv2-extras {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.dv2-plan {
    display: inline-flex;
    align-items: center;
    max-width: 150px;
    padding: 4px 10px;
    border-radius: 999px;
    background: #fff4d6;
    color: #92400e;
    font-size: 11px;
    font-weight: 700;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border: 1px solid #fde68a;
    margin: 0 2px;
}

:global(.dv2-app--dark) .dv2-plan {
    background: rgba(251, 191, 36, 0.16);
    color: #fcd34d;
    border-color: rgba(251, 191, 36, 0.35);
}

.dv2-icon-btn {
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--brand-on-header, var(--dv2-navy, #1a2b4c));
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
}

.dv2-icon-btn--ghost {
    background: transparent;
}

.dv2-icon-btn:hover {
    background: transparent;
    color: var(--brand-on-header, var(--dv2-primary, #2f6fed));
}

.dv2-notify-badge {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 15px;
    height: 15px;
    padding: 0 3px;
    border-radius: 999px;
    background: #ef4444;
    color: #fff;
    font-size: 9px;
    font-weight: 800;
    line-height: 15px;
    text-align: center;
}

.dv2-notify-badge.is-wide {
    min-width: 18px;
}

.dv2-notify-badge.is-extra {
    min-width: 24px;
    font-size: 8px;
}

.dv2-dropdown {
    position: relative;
}

.dv2-dropdown__panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 60;
    background: var(--dv2-surface, #fff);
    border: 1px solid var(--dv2-border, #e6ebf3);
    border-radius: 14px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
    overflow: hidden;
}

.dv2-user-btn {
    display: inline-flex;
    align-items: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--dv2-navy, #1a2b4c);
    cursor: pointer;
    font-family: inherit;
}

.dv2-user-btn:hover {
    background: transparent;
    color: var(--dv2-primary, #2f6fed);
}

.dv2-user-btn :deep(.avatar),
.dv2-user-btn :deep(.avatar-img) {
    width: 32px !important;
    height: 32px !important;
    border-radius: 50%;
}

.dv2-user-panel {
    min-width: 200px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.dv2-user-panel__item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 10px 12px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--dv2-navy, #1a2b4c);
    text-decoration: none;
    font-size: 13px;
    font-weight: 650;
    font-family: inherit;
    cursor: pointer;
}

.dv2-user-panel__item:hover {
    background: var(--dv2-surface-2, #f3f6fc);
}

.dv2-user-panel__item--danger {
    color: #dc2626;
}

.dv2-notify-panel {
    width: min(360px, 92vw);
}

.dv2-notify-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid var(--dv2-border, #e6ebf3);
}

.dv2-notify-panel__head strong {
    font-size: 14px;
}

.dv2-notify-panel__count {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: var(--brand-primary-soft, var(--dv2-primary-soft, #e8f0fe));
    color: var(--brand-primary, var(--dv2-primary, #2f6fed));
    font-size: 11px;
    font-weight: 800;
    line-height: 20px;
    text-align: center;
}

.dv2-notify-panel__list {
    max-height: 320px;
    overflow-y: auto;
    background: var(--dv2-surface, var(--brand-surface-bg, transparent));
    scrollbar-width: thin;
    scrollbar-color: rgba(var(--brand-primary-rgb, 67, 97, 238), 0.45)
        var(--dv2-surface-2, var(--brand-surface-item-bg, #f3f6fc));
}

.dv2-notify-panel__list::-webkit-scrollbar {
    width: 6px;
}

.dv2-notify-panel__list::-webkit-scrollbar-track {
    background: var(--dv2-surface-2, var(--brand-surface-item-bg, #f3f6fc));
    border-radius: 999px;
}

.dv2-notify-panel__list::-webkit-scrollbar-thumb {
    background: rgba(var(--brand-primary-rgb, 67, 97, 238), 0.4);
    border-radius: 999px;
}

.dv2-notify-panel__list::-webkit-scrollbar-thumb:hover {
    background: var(--brand-primary, #4361ee);
}

.dv2-notify-sentinel {
    height: 1px;
    width: 100%;
    pointer-events: none;
}

.dv2-notify-loading-more {
    padding: 10px 14px 14px;
    text-align: center;
    color: var(--dv2-muted, #7a8699);
    font-size: 12px;
}

.dv2-notify-empty {
    padding: 28px 16px;
    text-align: center;
    color: var(--dv2-muted, #7a8699);
    font-size: 13px;
}

.dv2-notify-item {
    width: 100%;
    display: flex;
    gap: 8px;
    align-items: flex-start;
    padding: 12px 14px;
    border: none;
    border-bottom: 1px solid var(--dv2-shortcut-border, #eef2f8);
    background: transparent;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    color: inherit;
}

.dv2-notify-item.is-unread {
    background: var(--brand-primary-soft, var(--dv2-primary-soft, #e8f0fe));
}

.dv2-notify-item__title {
    margin: 0 0 4px;
    font-size: 13px;
    font-weight: 700;
    color: var(--dv2-navy, #1a2b4c);
}

.dv2-notify-item__msg {
    margin: 0 0 6px;
    font-size: 12px;
    color: var(--dv2-muted, #7a8699);
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-wrap;
    word-break: break-word;
}

.dv2-notify-item__msg.is-expanded {
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
}

.dv2-notify-item__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}

.dv2-notify-item__expand {
    border: none;
    background: transparent;
    padding: 0;
    color: var(--brand-primary, var(--dv2-primary, #2f6fed));
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
}

.dv2-notify-item__expand:hover {
    text-decoration: underline;
}

.dv2-notify-item small {
    color: var(--dv2-muted, #7a8699);
    font-size: 11px;
    margin-left: auto;
}

.dv2-notify-item__remove {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border: none;
    padding: 0;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--dv2-muted, #7a8699);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    font-family: inherit;
}

.dv2-notify-item__remove:hover {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
}

.dv2-notify-panel__footer {
    width: 100%;
    border: none;
    border-top: 1px solid var(--dv2-border, #e6ebf3);
    background: transparent;
    padding: 10px;
    color: var(--brand-primary, var(--dv2-primary, #2f6fed));
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
}

.dv2-notify-panel__footer:hover {
    background: var(--brand-primary-soft, var(--dv2-surface-2, #f3f6fc));
}

@media (max-width: 992px) {
    .dv2-plan {
        display: none;
    }
}
</style>

<style>
/* Scrollbar do painel de notificações (webkit + Firefox) — dark */
body.dark .dv2-notify-panel__list,
[data-bs-theme='dark'] .dv2-notify-panel__list {
    scrollbar-color: rgba(var(--brand-primary-rgb, 67, 97, 238), 0.55)
        var(--brand-surface-item-bg-dark, #1b2e4b);
}

body.dark .dv2-notify-panel__list::-webkit-scrollbar-track,
[data-bs-theme='dark'] .dv2-notify-panel__list::-webkit-scrollbar-track {
    background: var(--brand-surface-item-bg-dark, #1b2e4b);
}

body.dark .dv2-notify-panel__list::-webkit-scrollbar-thumb,
[data-bs-theme='dark'] .dv2-notify-panel__list::-webkit-scrollbar-thumb {
    background: rgba(var(--brand-primary-rgb, 67, 97, 238), 0.5);
}

body.dark .dv2-notify-panel__list::-webkit-scrollbar-thumb:hover,
[data-bs-theme='dark'] .dv2-notify-panel__list::-webkit-scrollbar-thumb:hover {
    background: var(--brand-primary, #4361ee);
}
</style>
